import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PostCard from "../components/PostCard";
import Trinca3em1QuoteModal from "../components/Trinca3em1QuoteModal";
import { api } from "../lib/api";
import { WHATSAPP_URL } from "../lib/constants";
import { usePageMeta } from "../lib/usePageMeta";
import {
  Sparkles, Compass, MapPin, ArrowRight, Search as SearchIcon,
  ShieldCheck, FileCheck2, Wallet, Award, ClipboardList, Globe2,
} from "lucide-react";
import WhatsAppIcon from "../components/WhatsAppIcon";

// Pricing: default separado / 3-em-1 (BRL). Trincas 01, 06, 21, 26 têm
// preços promocionais mais baixos (destinos com boa combinação de câmbio
// e logística de aéreo). Além disso, algumas trincas (PLUS_250_CARDS) têm
// R$ 250 a mais que a faixa da sua categoria (variação natural do mercado).
// Editor pode ajustar por trinca individualmente.
const PRICE_DEFAULT = { separate: 7999, combined: 9999 };
const PRICE_PROMO   = { separate: 5999, combined: 7899 };
const PROMO_CARDS   = new Set([1, 6, 21, 26]);
const PLUS_250_CARDS = new Set([3, 9, 14, 18, 22]);
const priceFor = (i) => {
  const base = PROMO_CARDS.has(i) ? PRICE_PROMO : PRICE_DEFAULT;
  if (PLUS_250_CARDS.has(i)) {
    return { separate: base.separate + 250, combined: base.combined + 250 };
  }
  return base;
};
const fmtBRL = (n) => n.toLocaleString("pt-BR", { maximumFractionDigits: 0 });

const HERO_IMG = "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?auto=format&fit=crop&w=1600&q=70";

// The 27 official trincas — same source of truth as the pauta series.
// Each has a slug used to tag articles and a subtle theme color.
const TRINCAS = [
  { i: 1,  cities: ["Amsterdã", "Paris", "Londres"],                slug: "amsterda-paris-londres",           theme: "classic",  region: "Europa Ocidental",   emoji: "🎡" },
  { i: 2,  cities: ["Roma", "Vaticano", "Suíça"],                    slug: "roma-vaticano-suica",              theme: "spiritual",region: "Cultural + Alpes",   emoji: "⛰️" },
  { i: 3,  cities: ["Bruxelas", "Colônia", "Roterdã"],               slug: "bruxelas-colonia-roterda",         theme: "gothic",   region: "Baixos Países",      emoji: "🍫" },
  { i: 4,  cities: ["Varsóvia", "Vilnius", "Riga"],                  slug: "varsovia-vilnius-riga",            theme: "eastern",  region: "Europa do Leste",    emoji: "🏰" },
  { i: 6,  cities: ["Viena", "Budapeste", "Praga"],                  slug: "viena-budapeste-praga",            theme: "imperial", region: "Império Habsburgo",  emoji: "🎼" },
  { i: 7,  cities: ["Bruxelas", "Amsterdã", "Luxemburgo"],           slug: "bruxelas-amsterda-luxemburgo",     theme: "gothic",   region: "Trio Benelux",       emoji: "🚂" },
  { i: 8,  cities: ["Berlim", "Munique", "Salzburgo"],               slug: "berlim-munique-salzburgo",         theme: "germanic", region: "Alemanha + Áustria", emoji: "🍺" },
  { i: 9,  cities: ["Nice", "Cannes", "Monte Carlo"],                slug: "nice-cannes-monte-carlo",          theme: "riviera",  region: "Riviera Francesa",   emoji: "🎬" },
  { i: 11, cities: ["Nápoles", "Sorrento", "Amalfi"],                slug: "napoles-sorrento-amalfi",          theme: "amalfi",   region: "Costa Amalfitana",   emoji: "🍋" },
  { i: 12, cities: ["Positano", "Ravello", "Capri"],                 slug: "positano-ravello-capri",           theme: "amalfi",   region: "Costa Amalfitana Íntima", emoji: "🍹" },
  { i: 13, cities: ["Nápoles", "Capri", "Salerno"],                  slug: "napoles-capri-salerno",            theme: "amalfi",   region: "Golfo de Nápoles",   emoji: "🐚" },
  { i: 14, cities: ["Bari", "Matera", "Amalfi"],                     slug: "bari-matera-amalfi",               theme: "amalfi",   region: "Sul da Itália Rústico", emoji: "🏘️" },
  { i: 15, cities: ["Londres", "Oxford", "Brighton"],                slug: "londres-oxford-brighton",          theme: "british",  region: "Sul da Inglaterra",  emoji: "☂️" },
  { i: 16, cities: ["Lisboa", "Porto", "Sintra"],                    slug: "lisboa-porto-sintra",              theme: "iberian",  region: "Portugal Completo",  emoji: "🍷" },
  { i: 17, cities: ["Paris", "Bruxelas", "Amsterdã"],                slug: "paris-bruxelas-amsterda",          theme: "classic",  region: "Rota do Eurostar",   emoji: "🚄" },
  { i: 18, cities: ["Milão", "Lucerna", "Nice"],                     slug: "milao-lucerna-nice",               theme: "alps",     region: "Milão → Alpes → Riviera", emoji: "🏔️" },
  { i: 19, cities: ["Lisboa", "Madri", "Toulouse"],                  slug: "lisboa-madri-toulouse",            theme: "iberian",  region: "Ibéria + Sul da França", emoji: "🌻" },
  { i: 20, cities: ["Budapeste", "Bratislava", "Cracóvia"],          slug: "budapeste-bratislava-cracovia",    theme: "imperial", region: "Grupo de Visegrad",  emoji: "🎭" },
  { i: 21, cities: ["Oslo", "Estocolmo", "Copenhague"],              slug: "oslo-estocolmo-copenhague",        theme: "nordic",   region: "Capitais Nórdicas",  emoji: "❄️" },
  { i: 22, cities: ["Tallin", "Riga", "Vilnius"],                    slug: "tallin-riga-vilnius",              theme: "eastern",  region: "Trio Báltico",       emoji: "🌲" },
  { i: 23, cities: ["Atenas", "Sófia", "Skopje"],                    slug: "atenas-sofia-skopje",              theme: "balkan",   region: "Balcãs Orientais",   emoji: "🕍" },
  { i: 25, cities: ["Zurique", "Vaduz", "Innsbruck"],                slug: "zurique-vaduz-innsbruck",          theme: "alps",     region: "Coração dos Alpes",  emoji: "🏔️" },
  { i: 26, cities: ["Estrasburgo", "Frankfurt", "Luxemburgo"],       slug: "estrasburgo-frankfurt-luxemburgo", theme: "germanic", region: "Vale do Reno",       emoji: "🌉" },
  { i: 27, cities: ["Lisboa", "Madri", "Barcelona"],                 slug: "lisboa-madri-barcelona",           theme: "iberian",  region: "Ibéria Vibrante",    emoji: "💃" },
];

const THEME_COLORS = {
  classic:        "#3B4A9C",
  spiritual:      "#7D4EC7",
  gothic:         "#4A5F41",
  eastern:        "#A0522D",
  orient:         "#C25F00",
  imperial:       "#8B3A62",
  germanic:       "#B85C38",
  riviera:        "#00A9B7",
  mediterranean:  "#0072BB",
  amalfi:         "#F0994B",
  british:        "#5A5D8B",
  iberian:        "#C2185B",
  alps:           "#3A7CA5",
  nordic:         "#2A5D5D",
  balkan:         "#A0463D",
};

const TRUST = [
  { icon: ShieldCheck, title: "Cadastur ativo",   body: "Ministério do Turismo." },
  { icon: FileCheck2,  title: "Contrato formal",  body: "CDC + reembolsável." },
  { icon: Wallet,      title: "Parcelamento 12×", body: "Sem juros no cartão." },
  { icon: Award,       title: "Escolha Orion",    body: "Especialista pessoal." },
];

export default function Internacional3em1() {
  const [q, setQ] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteTrincaSlug, setQuoteTrincaSlug] = useState("");
  const [latestPosts, setLatestPosts] = useState([]);
  const openQuote = (slug = "") => {
    setQuoteTrincaSlug(slug);
    setQuoteOpen(true);
  };

  // Fetch the latest published articles from the whole 3-em-1 series parent.
  // The `parent=` param on /api/posts expands to all 27 trinca child slugs,
  // so any newly-generated article from the pautas backlog surfaces here
  // without needing a manual list.
  useEffect(() => {
    let alive = true;
    api
      .get("/posts", { params: { parent: "internacional-3-em-1", limit: 6 } })
      .then((r) => { if (alive) setLatestPosts(r.data?.items || []); })
      .catch(() => { if (alive) setLatestPosts([]); });
    return () => { alive = false; };
  }, []);
  usePageMeta(
    "Internacional 3 em 1 · Viagens Orion",
    "27 combinações de 3 cidades europeias em uma só viagem: Amsterdã + Paris + Londres, Roma + Vaticano + Suíça, Lisboa + Porto + Sintra e muito mais. Roteiros exclusivos com Cadastur ativo."
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return TRINCAS;
    const needle = q.toLowerCase();
    return TRINCAS.filter((t) =>
      t.cities.some((c) => c.toLowerCase().includes(needle)) ||
      t.region.toLowerCase().includes(needle)
    );
  }, [q]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1" data-testid="int3em1-page">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#1a0e3d] via-[#2C1139] to-[#0d0413] text-white">
          <span className="absolute inset-0 opacity-25" aria-hidden style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }} />
          <span className="absolute inset-0 bg-gradient-to-b from-[#1a0e3d]/95 via-[#2C1139]/75 to-[#0d0413]/85" aria-hidden />
          <span className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-orion-gold/25 blur-3xl" aria-hidden />
          <span className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-orion-orange/20 blur-3xl" aria-hidden />

          <div className="relative container-orion py-16 md:py-28 max-w-4xl text-center mx-auto">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-5">
              <Compass size={13} /> Coleção Exclusiva Orion
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] mx-auto">
              Internacional <span className="text-orion-gold">3 em 1</span>.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 mx-auto leading-relaxed">
              Uma viagem, três cidades icônicas. 27 combinações curadas pela Orion para
              quem tem pouco tempo e quer o máximo da Europa (e além) em cada trecho.
              Voo, transporte terrestre, hospedagem e roteiro fechados.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <a
                href="#trincas"
                className="inline-flex items-center gap-2 bg-orion-gold text-orion-purple px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#c19a3f] transition-all shadow-[0_14px_38px_rgba(216,167,73,0.45)] hover:-translate-y-0.5"
                data-testid="int3-see-all"
              >
                Ver as 27 combinações <ArrowRight size={14} />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:border-orion-gold hover:text-orion-gold transition-colors"
                data-testid="int3-wa"
              >
                <WhatsAppIcon size={14} color="#ffffff" /> Falar com um Especialista
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-[10px] uppercase tracking-[0.3em] text-white/75">
              <span className="inline-flex items-center gap-2"><ShieldCheck size={12} className="text-orion-gold" /> Cadastur ativo</span>
              <span className="inline-flex items-center gap-2"><Globe2 size={12} className="text-orion-gold" /> +27 combinações</span>
              <span className="inline-flex items-center gap-2"><Award size={12} className="text-orion-gold" /> Especialista Orion</span>
              <span className="inline-flex items-center gap-2"><Wallet size={12} className="text-orion-gold" /> Parcelamento em 12×</span>
            </div>
          </div>
        </section>

        {/* TRUST STRIP */}
        <section className="border-y border-[#EFEAE0] bg-[#FAF7F0]">
          <div className="container-orion py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {TRUST.map((t, i) => (
              <div key={i} className="flex flex-col items-center gap-2" data-testid={`int3-trust-${i}`}>
                <t.icon size={22} className="text-orion-orange" />
                <div className="font-serif text-orion-purple text-sm md:text-base">{t.title}</div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#594A63] leading-snug">{t.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* TRINCAS GRID */}
        <section id="trincas" className="container-orion pt-20 md:pt-28 pb-10 md:pb-12">
          <div className="max-w-3xl mx-auto mb-12 text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Descubra sua trinca</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              27 combinações. Uma viagem. Memórias que a vida inteira não apaga.
            </h2>
            <p className="mt-4 text-[#594A63] text-base md:text-lg leading-relaxed">
              Filtre por cidade ou região e escolha o roteiro que combina com o seu momento.
              Cada trinca é operada pela Orion com voo, hospedagem, deslocamento interno e
              suporte 24h em português.
            </p>
          </div>

          {/* Search filter */}
          <div className="max-w-md mx-auto mb-10 relative" data-testid="int3-filter">
            <SearchIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A8191]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              type="text"
              placeholder="Filtrar por cidade ou região (ex.: Paris, Alpes, Amalfi…)"
              className="w-full pl-11 pr-4 py-3 text-sm bg-white border border-[#EFEAE0] rounded-full focus:outline-none focus:border-orion-orange focus:ring-2 focus:ring-orion-orange/15 transition-all"
              data-testid="int3-search-input"
            />
          </div>

          {/* Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="int3-grid">
            {filtered.map((t) => {
              const color = THEME_COLORS[t.theme] || "#3B4A9C";
              const price = priceFor(t.i);
              return (
                <div
                  key={t.slug}
                  className="int3-card group"
                  style={{ "--card-color": color }}
                  data-testid={`int3-card-${t.slug}`}
                >
                  <Link
                    to={`/categoria/${t.slug}`}
                    className="int3-card-top-link"
                    data-testid={`int3-card-cat-${t.slug}`}
                  >
                    <div className="int3-card-top">
                      <span className="int3-card-num">{String(t.i).padStart(2, "0")}</span>
                      <span className="int3-card-emoji" aria-hidden>{t.emoji}</span>
                    </div>
                  </Link>
                  <div className="int3-card-body">
                    <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-orion-orange mb-2">
                      {t.region}
                    </div>
                    <Link to={`/categoria/${t.slug}`} className="block hover:opacity-80 transition-opacity">
                      <h3 className="font-serif text-lg text-orion-purple leading-tight">
                        {t.cities.map((c, idx) => (
                          <span key={c}>
                            {c}
                            {idx < 2 && <span className="text-orion-orange"> + </span>}
                          </span>
                        ))}
                      </h3>
                    </Link>

                    {/* Preços */}
                    <div className="mt-4 space-y-1.5 text-[12px] leading-snug">
                      <div className="text-[#594A63]">
                        Cada destino separado <span className="text-[10px] text-[#8A8191]">·</span>{" "}
                        <span className="font-semibold text-orion-purple">a partir de R$ {fmtBRL(price.separate)}</span>
                      </div>
                      <div className="flex flex-wrap items-baseline gap-x-1.5">
                        <span className="text-[#594A63]">No 3 em 1 (os 3 juntos)</span>
                        <span className="font-bold text-orion-orange text-[13px]">a partir de R$ {fmtBRL(price.combined)}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => openQuote(t.slug)}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] italic text-orion-purple font-semibold hover:text-orion-orange transition-colors underline underline-offset-2 decoration-orion-orange/40"
                        data-testid={`int3-card-consult-${t.slug}`}
                      >
                        consulte o roteiro
                        <ArrowRight size={11} />
                      </button>
                    </div>

                    <Link
                      to={`/categoria/${t.slug}`}
                      className="mt-4 pt-3 border-t border-[#EFEAE0] flex items-center justify-between hover:opacity-90 transition-opacity"
                    >
                      <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#594A63] group-hover:text-orion-orange transition-colors">
                        Ver artigos & roteiros
                      </span>
                      <ArrowRight size={14} className="text-orion-orange transform group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-14 text-[#8A8191]" data-testid="int3-empty">
              Nenhuma trinca encontrada para "<strong>{q}</strong>". A Orion monta combinações
              personalizadas —{" "}
              <button
                type="button"
                onClick={() => openQuote("")}
                className="text-orion-orange font-semibold hover:underline"
                data-testid="int3-empty-cta"
              >
                consulte um especialista
              </button>.
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="relative bg-gradient-to-br from-[#c5a059] via-[#c19a3f] to-[#8a6d24] text-white overflow-hidden">
          <div className="relative container-orion py-12 md:py-16 text-center max-w-3xl">
            <Sparkles size={26} className="mx-auto text-white mb-4" />
            <h2 className="font-serif text-2xl md:text-4xl leading-tight">
              Sua trinca dos sonhos ainda não está aqui?
            </h2>
            <p className="mt-4 text-white/95 text-sm md:text-base">
              A Orion monta roteiros personalizados de 3 cidades sob medida. Envie a combinação
              que você imagina e devolvemos proposta com voo, hospedagem e transporte em minutos.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => openQuote("")}
                className="inline-flex items-center gap-2 bg-white text-orion-purple px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-orion-cream transition-all hover:-translate-y-0.5 shadow-[0_14px_38px_rgba(0,0,0,0.25)]"
                data-testid="int3-cta-quote"
              >
                <Sparkles size={14} className="text-orion-orange" /> Escolher Minha Trinca com a Orion
              </button>
              <Link
                to="/sobre"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-white/10 transition-colors"
              >
                Conhecer a Orion
              </Link>
            </div>
          </div>
        </section>

        {/* ÚLTIMOS ARTIGOS DA SÉRIE 3 EM 1 */}
        {latestPosts.length > 0 && (
          <section className="container-orion py-16 md:py-20" data-testid="int3-latest-posts">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-orion-orange mb-2">
                  Últimos publicados
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-orion-purple leading-tight">
                  Artigos recentes da série 3 em 1
                </h2>
                <p className="mt-2 text-sm text-[#594A63] max-w-2xl">
                  Roteiros, guias de preço, deslocamento entre cidades e dicas práticas — os últimos guias que a Orion publicou nas 27 trincas europeias.
                </p>
              </div>
              <Link
                to="/categoria/internacional-3-em-1"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.22em] font-bold text-orion-orange hover:text-[#E04828]"
                data-testid="int3-latest-see-all"
              >
                Ver todos <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestPosts.map((p) => (
                <PostCard key={p.id} post={p} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />

      {quoteOpen && (
        <Trinca3em1QuoteModal
          onClose={() => setQuoteOpen(false)}
          trincas={TRINCAS}
          defaultTrincaSlug={quoteTrincaSlug}
        />
      )}

      <style>{`
        .int3-card {
          display: block;
          background: #fff;
          border: 1px solid #EFEAE0;
          border-radius: 8px;
          overflow: hidden;
          transition: transform 240ms ease, box-shadow 240ms ease, border-color 240ms ease;
        }
        .int3-card:hover {
          transform: translateY(-4px);
          border-color: var(--card-color);
          box-shadow: 0 22px 48px -20px rgba(44,17,57,0.22), 0 0 0 1px var(--card-color);
        }
        .int3-card-top {
          position: relative;
          height: 92px;
          background: linear-gradient(135deg, var(--card-color), color-mix(in srgb, var(--card-color) 55%, black));
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 20px;
          overflow: hidden;
        }
        .int3-card-top::before {
          content: ""; position: absolute; inset: 0;
          background: radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2), transparent 60%);
        }
        .int3-card-num {
          position: relative;
          font-family: 'Playfair Display', serif;
          font-size: 44px; line-height: 1;
          color: rgba(255,255,255,0.55);
          font-weight: 700;
        }
        .int3-card-emoji {
          position: relative;
          font-size: 42px; line-height: 1;
          filter: drop-shadow(0 4px 12px rgba(0,0,0,0.35));
        }
        .int3-card-body { padding: 20px 22px 22px 22px; }
      `}</style>
    </div>
  );
}
